// ---------------------------------------------------------------------------
// "Which agreement do I need?" — a guided GDPR role/instrument classifier.
//
// Data stewards routinely have to decide *which* instrument a data-sharing
// arrangement calls for before they can draft anything: a processor DPA
// (Art. 28), a joint-controller arrangement (Art. 26), or a controller-to-
// controller data-sharing agreement. This flow walks them through the
// determining questions (based on the EDPB Guidelines 07/2020 on the concepts
// of controller and processor) and outputs a reasoned recommendation, with
// notes on special-category data (Art. 9) and international transfers
// (Chapter V).
//
// It is frontend-only: the value is the on-screen recommendation, so there is
// no matching contract generator and no `outputLabel`.
// ---------------------------------------------------------------------------

import { ItemDefinition } from './utils';
import { END, endItem, goTo, branchOnYesNo } from './authoring';

const id = 'agreement-classifier-0.0.1';
const name = 'Which agreement do I need?';
const version = '0.0.1';

// --- Field ids and option values, referenced by the classifier + tests -------
export const IDS = {
  personalData: 'personal_data',
  dataNature: 'data_nature',
  relationship: 'role_relationship',
  ownPurposes: 'own_purposes',
  jointlyDecide: 'jointly_decide',
  result: 'result',
};

export const RELATIONSHIP = {
  PROCESSOR: 'processor',
  JOINT: 'joint',
  SEPARATE: 'separate',
  UNSURE: 'unsure',
};

export const LOCATION = {
  EEA: 'eea',
  ADEQUACY: 'adequacy',
  THIRD_COUNTRY: 'third_country',
};

// --- Pure classifier --------------------------------------------------------
// Reads the collected answers and returns the determination. Kept free of any
// engine/React coupling so it can be unit-tested directly. Tolerates partial
// state (returns `null` kind while the flow is mid-answer).
//
// Returns: { kind, specialCategory, recipientLocation }
//   kind: 'anonymous' | 'processor' | 'joint' | 'separate' | null
export function classifyAgreement(states) {
  const val = (itemId, i = 0) => states?.[itemId]?.values?.[i];

  const specialCategory = val(IDS.dataNature, 0) === 1;
  const recipientLocation = val(IDS.dataNature, 1);

  // No personal data in scope -> GDPR data-sharing instruments don't apply.
  if (val(IDS.personalData, 0) !== 1) {
    return { kind: 'anonymous', specialCategory: false, recipientLocation: undefined };
  }

  let kind = null;
  const rel = val(IDS.relationship, 0);
  if (rel === RELATIONSHIP.PROCESSOR || rel === RELATIONSHIP.JOINT || rel === RELATIONSHIP.SEPARATE) {
    kind = rel;
  } else if (rel === RELATIONSHIP.UNSURE) {
    // Guided disambiguation: does the other party pursue its own purposes?
    if (val(IDS.ownPurposes, 0) === 0) {
      kind = 'processor';
    } else if (val(IDS.ownPurposes, 0) === 1) {
      kind = val(IDS.jointlyDecide, 0) === 1 ? 'joint' : 'separate';
    }
  }

  return { kind, specialCategory, recipientLocation };
}

// --- Result copy ------------------------------------------------------------
// Each entry is turned into a `noInput` field. Only **bold**, *italic* and
// links render as markdown (the renderer strips lists/headings), so each
// bullet is its own field.
const RESULT_COPY = {
  anonymous: {
    headline: '**No data-sharing agreement is required under the GDPR.**',
    lines: [
      "You indicated that the arrangement does **not** involve personal data. If the data is truly anonymous — irreversibly stripped of any means to identify an individual (Recital 26) — the GDPR does not apply.",
      '**Watch out:** *pseudonymised* data (key-coded, hashed, or re-identifiable by anyone) is still personal data. If in doubt, treat it as personal data and re-run this check.',
    ],
  },
  processor: {
    headline: '**Recommended instrument: a Data Processing Agreement (Art. 28 GDPR).**',
    lines: [
      'The other organisation acts as your **processor** — it handles the personal data only on your documented instructions and pursues no purpose of its own. Your organisation remains the **controller**.',
      'Article 28(3) makes a written contract **mandatory**. It must cover: subject-matter and duration, nature and purpose, type of data and categories of data subjects, and the controller’s obligations and rights.',
      'It must also bind the processor to: act only on instructions, ensure confidentiality, apply Art. 32 security measures, respect sub-processor rules, assist with data-subject rights and Art. 32–36 duties, and delete or return the data at the end.',
    ],
  },
  joint: {
    headline: '**Recommended instrument: a Joint Controller Arrangement (Art. 26 GDPR).**',
    lines: [
      'The two organisations **jointly determine** the purposes and means of the processing, which makes you **joint controllers**.',
      'Article 26 requires a transparent arrangement that allocates responsibilities — in particular for exercising data-subject rights and for the Art. 13–14 information duties.',
      'The **essence** of that arrangement must be made available to the data subjects, and they may exercise their rights against either controller regardless of its terms.',
    ],
  },
  separate: {
    headline: '**Recommended instrument: a controller-to-controller Data Sharing Agreement.**',
    lines: [
      'Each organisation independently determines its **own** purposes for the data, so you are **separate (independent) controllers** — a controller-to-controller disclosure rather than joint control or processing.',
      'The GDPR does **not** mandate a contract here, but a data-sharing agreement is strongly recommended: it should record the lawful basis for the disclosure, the purposes each party may use the data for, security expectations, and how data-subject requests are handled.',
      'Confirm you have a **lawful basis** (Art. 6) for the disclosure and that it is compatible with the purpose the data was originally collected for.',
    ],
  },
};

const TRANSFER_NOTE = {
  [LOCATION.EEA]:
    'The recipient is inside the EEA, so no Chapter V transfer mechanism is needed.',
  [LOCATION.ADEQUACY]:
    '**International transfer:** the recipient is in a country covered by an **adequacy decision**, which provides the transfer safeguard — no additional tool is required, but verify the decision still applies to your data.',
  [LOCATION.THIRD_COUNTRY]:
    '**International transfer (Chapter V):** the recipient is in a third country **without** an adequacy decision. You will also need an appropriate transfer tool (typically **Standard Contractual Clauses**, Art. 46) plus a **Transfer Impact Assessment**.',
};

const SPECIAL_CATEGORY_NOTE =
  '**Special-category data (Art. 9):** you must identify a specific Art. 9(2) condition in addition to your Art. 6 lawful basis, apply heightened safeguards, and a **Data Protection Impact Assessment** (Art. 35) is very likely required.';

const DISCLAIMER =
  '*This is decision-support guidance based on the answers you gave, not legal advice. Confirm the outcome with your DPO or legal team before drafting.*';

function buildResultFields(states) {
  const { kind, specialCategory, recipientLocation } = classifyAgreement(states);
  const copy = RESULT_COPY[kind] || RESULT_COPY.separate;

  const fields = [{ type: 'noInput', label: copy.headline }];
  copy.lines.forEach((line) => fields.push({ type: 'noInput', label: line }));

  // Notes only apply once we know personal data is in scope.
  if (kind && kind !== 'anonymous') {
    if (specialCategory) {
      fields.push({ type: 'noInput', label: SPECIAL_CATEGORY_NOTE });
    }
    if (recipientLocation && TRANSFER_NOTE[recipientLocation]) {
      fields.push({ type: 'noInput', label: TRANSFER_NOTE[recipientLocation] });
    }
  }

  fields.push({ type: 'noInput', label: DISCLAIMER });
  return fields;
}

// --- Items ------------------------------------------------------------------

const intro = new ItemDefinition({
  id: 'intro',
  title: 'Which agreement do I need?',
  fields: [
    {
      type: 'noInput',
      label:
        "Answer a few questions about your data-sharing arrangement and this tool will tell you **which instrument** it calls for — a processor agreement, a joint-controller arrangement, or a controller-to-controller data-sharing agreement — and flag any transfer or special-category considerations.",
    },
  ],
  chooseNext: goTo(IDS.personalData),
});

const personal_data = new ItemDefinition({
  id: IDS.personalData,
  title: 'Personal data',
  fields: [
    {
      label: 'Does the arrangement involve personal data?',
      explanation:
        'Any information relating to an identified or identifiable living person. **Pseudonymised** (key-coded) data still counts; only *irreversibly anonymous* data does not.',
      type: 'yesno',
    },
  ],
  // No personal data -> jump straight to the result (anonymous outcome).
  chooseNext: branchOnYesNo(0, IDS.dataNature, IDS.result),
});

const data_nature = new ItemDefinition({
  id: IDS.dataNature,
  title: 'Nature of the data and the recipient',
  fields: [
    {
      label: 'Does it include special-category or criminal-offence data?',
      explanation:
        'Special categories (Art. 9): health, genetics, biometrics used to identify someone, racial/ethnic origin, political/religious/philosophical beliefs, trade-union membership, sex life or sexual orientation. Also criminal convictions/offences (Art. 10).',
      type: 'yesno',
    },
    {
      label: 'Where is the other organisation established?',
      type: 'radio',
      options: [
        { key: 'eea', text: 'Inside the EEA', value: LOCATION.EEA },
        { key: 'adq', text: 'A country with an EU adequacy decision (e.g. UK, Switzerland)', value: LOCATION.ADEQUACY },
        { key: 'third', text: 'Another third country (no adequacy decision)', value: LOCATION.THIRD_COUNTRY },
      ],
    },
  ],
  chooseNext: goTo(IDS.relationship),
});

const role_relationship = new ItemDefinition({
  id: IDS.relationship,
  title: 'Who decides why and how the data is processed?',
  fields: [
    {
      label: 'Which best describes how the two organisations decide the **purposes and means** of the processing?',
      explanation:
        'This is the question that determines each party’s role under the GDPR. Pick the closest fit, or “I’m not sure” to be guided through it.',
      type: 'radio',
      options: [
        {
          key: 'proc',
          text: 'Only my organisation decides; the other party just acts on our documented instructions',
          value: RELATIONSHIP.PROCESSOR,
        },
        {
          key: 'joint',
          text: 'We decide the purposes and means together / jointly',
          value: RELATIONSHIP.JOINT,
        },
        {
          key: 'sep',
          text: 'Each organisation independently decides its own purposes',
          value: RELATIONSHIP.SEPARATE,
        },
        { key: 'unsure', text: 'I’m not sure', value: RELATIONSHIP.UNSURE },
      ],
    },
  ],
  chooseNext: function (states) {
    const rel = states[this.id].values[0];
    return rel === RELATIONSHIP.UNSURE ? IDS.ownPurposes : IDS.result;
  },
});

const own_purposes = new ItemDefinition({
  id: IDS.ownPurposes,
  title: 'The other party’s purposes',
  fields: [
    {
      label: 'Does the other party use the personal data for any of its **own** purposes?',
      explanation:
        'Beyond carrying out your instructions. A party that only follows instructions and pursues no purpose of its own is a *processor*; any own purpose points to (joint or separate) controllership.',
      type: 'yesno',
    },
  ],
  // No own purpose -> processor; otherwise dig into whether it's joint.
  chooseNext: branchOnYesNo(0, IDS.jointlyDecide, IDS.result),
});

const jointly_decide = new ItemDefinition({
  id: IDS.jointlyDecide,
  title: 'Shared decision-making',
  fields: [
    {
      label: 'Did the two organisations **jointly** design or decide the processing?',
      explanation:
        'A shared purpose, a jointly designed study or platform, or co-decided means point to *joint controllers* (Art. 26). If each side decided independently, you are *separate controllers*.',
      type: 'yesno',
    },
  ],
  chooseNext: goTo(IDS.result),
});

const result = new ItemDefinition({
  id: IDS.result,
  title: 'Recommendation',
  fields: [],
  buildFields: function (states) {
    return buildResultFields(states);
  },
  chooseNext: goTo(END),
});

const the_end = endItem('Done');

const items = [
  intro,
  personal_data,
  data_nature,
  role_relationship,
  own_purposes,
  jointly_decide,
  result,
  the_end,
];

const loopsDefinitions = {};

const flow = { id, name, version, items, loopsDefinitions, disclaimerText: '' };
export default flow;
